import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';

function PurchaseManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'rfq');
  const [loading, setLoading] = useState(false);

  // Data states
  const [rfqs, setRfqs] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [dashboard, setDashboard] = useState(null);

  // Modal states
  const [showCreateRFQModal, setShowCreateRFQModal] = useState(false);
  const [selectedApprovedReqs, setSelectedApprovedReqs] = useState([]);
  const [approvedRequisitions, setApprovedRequisitions] = useState([]);

  const tabs = [
    { id: 'rfq', name: 'RFQ', icon: '📝', count: rfqs.length },
    { id: 'quotations', name: 'Quotations', icon: '💰', count: quotations.length },
    { id: 'po', name: 'Purchase Orders', icon: '📄', count: pos.length },
    { id: 'grn', name: 'GRN', icon: '📦', count: grns.length }
  ];

  useEffect(() => {
    fetchDashboard();
    if (activeTab === 'rfq') {
      fetchRFQs();
    } else if (activeTab === 'quotations') {
      fetchQuotations();
    } else if (activeTab === 'po') {
      fetchPOs();
    } else if (activeTab === 'grn') {
      fetchGRNs();
    }
  }, [activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/purchase/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchRFQs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/rfqs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRfqs(response.data.rfqs || []);
    } catch (error) {
      console.error('Error fetching RFQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/quotations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setQuotations(response.data.quotations || []);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPOs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/purchase-orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPos(response.data.purchase_orders || []);
    } catch (error) {
      console.error('Error fetching POs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGRNs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/grns`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGrns(response.data.grns || []);
    } catch (error) {
      console.error('Error fetching GRNs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedRequisitions = async () => {
    try {
      const response = await axios.get(`${API}/api/requisitions?status=Approved`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const reqs = response.data.requisitions || [];
      // Filter requisitions that have parts needing purchase
      const needsPurchase = reqs.filter(req => 
        req.spare_parts?.some(part => 
          part.approval_status === 'Approved' && part.stock_status === 'Need_Purchase'
        )
      );
      setApprovedRequisitions(needsPurchase);
    } catch (error) {
      console.error('Error fetching approved requisitions:', error);
    }
  };

  const handleCreateRFQ = async () => {
    if (selectedApprovedReqs.length === 0) {
      alert('Please select at least one requisition');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/rfq/create`,
        {
          requisition_ids: selectedApprovedReqs,
          supplier_ids: ['SUP001', 'SUP002'], // TODO: Make this dynamic
          delivery_location: 'TRC Warehouse, Mumbai',
          payment_terms: 'Net_30',
          notes: 'RFQ for approved spare parts'
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      alert('RFQ created successfully!');
      setShowCreateRFQModal(false);
      setSelectedApprovedReqs([]);
      fetchRFQs();
      fetchDashboard();
    } catch (error) {
      console.error('Error creating RFQ:', error);
      alert('Failed to create RFQ: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Sent': 'bg-blue-100 text-blue-800',
      'Quotes_Received': 'bg-green-100 text-green-800',
      'Closed': 'bg-purple-100 text-purple-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Submitted': 'bg-blue-100 text-blue-800',
      'Accepted': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Sent_To_Supplier': 'bg-blue-100 text-blue-800',
      'Acknowledged': 'bg-green-100 text-green-800',
      'Delivered': 'bg-green-100 text-green-800',
      'Completed': 'bg-purple-100 text-purple-800',
      'Pending_Inspection': 'bg-yellow-100 text-yellow-800',
      'QC_In_Progress': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <h1 className="text-3xl font-bold" style={{ color: ZOBOX_PRIMARY }}>
              Purchase Management
            </h1>
            <p className="text-gray-600 mt-1">
              Complete procurement workflow: RFQ → Quotation → PO → GRN
            </p>
          </div>
        </div>

        {/* Dashboard Cards */}
        {dashboard && (
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending RFQs</p>
                    <p className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>{dashboard.pending_rfqs}</p>
                  </div>
                  <div className="text-3xl">📝</div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Quotations</p>
                    <p className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>{dashboard.active_quotations}</p>
                  </div>
                  <div className="text-3xl">💰</div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending POs</p>
                    <p className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>{dashboard.pending_pos}</p>
                  </div>
                  <div className="text-3xl">📄</div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Purchase Value</p>
                    <p className="text-xl font-bold text-green-600">₹{dashboard.total_purchase_value.toLocaleString()}</p>
                  </div>
                  <div className="text-3xl">💵</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="border-b">
            <div className="flex gap-4">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-4 py-3 font-medium transition-colors relative flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-b-2 text-[#2b3380]'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  style={activeTab === tab.id ? { borderColor: ZOBOX_PRIMARY } : {}}
                >
                  <span>{tab.icon}</span>
                  {tab.name}
                  {tab.count > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {loading ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#2b3380]"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : (
            <>
              {/* RFQ Tab */}
              {activeTab === 'rfq' && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                      <h2 className="text-xl font-semibold">Request for Quotations</h2>
                      <button
                        onClick={() => {
                          fetchApprovedRequisitions();
                          setShowCreateRFQModal(true);
                        }}
                        className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                        style={{ backgroundColor: ZOBOX_PRIMARY }}
                      >
                        + Create RFQ
                      </button>
                    </div>
                  </div>

                  {rfqs.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="text-6xl mb-4">📝</div>
                      <h3 className="text-lg font-semibold mb-2">No RFQs Yet</h3>
                      <p className="text-gray-600 mb-4">Create RFQ from approved requisitions</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFQ Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Suppliers</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {rfqs.map((rfq) => (
                            <tr key={rfq.rfq_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                                  {rfq.rfq_number}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {rfq.items?.length || 0} items
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {rfq.supplier_ids?.length || 0} suppliers
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(rfq.status)}`}>
                                  {rfq.status.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(rfq.created_at).toLocaleDateString('en-GB')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button className="text-[#2b3380] hover:text-blue-900">View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Quotations Tab */}
              {activeTab === 'quotations' && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b">
                    <h2 className="text-xl font-semibold">Quotations</h2>
                  </div>

                  {quotations.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="text-6xl mb-4">💰</div>
                      <h3 className="text-lg font-semibold mb-2">No Quotations Yet</h3>
                      <p className="text-gray-600">Quotations will appear here once suppliers respond to RFQs</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quotation #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFQ</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {quotations.map((quot) => (
                            <tr key={quot.quotation_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                                  {quot.quotation_number}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {quot.rfq_number}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {quot.supplier_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                ₹{quot.total_amount.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(quot.status)}`}>
                                  {quot.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(quot.submitted_at).toLocaleDateString('en-GB')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button className="text-[#2b3380] hover:text-blue-900">View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Purchase Orders Tab */}
              {activeTab === 'po' && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b">
                    <h2 className="text-xl font-semibold">Purchase Orders</h2>
                  </div>

                  {pos.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="text-6xl mb-4">📄</div>
                      <h3 className="text-lg font-semibold mb-2">No Purchase Orders Yet</h3>
                      <p className="text-gray-600">POs will be created after selecting best quotations</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {pos.map((po) => (
                            <tr key={po.po_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                                  {po.po_number}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {po.supplier_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {po.items?.length || 0} items
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                ₹{po.total_amount.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(po.status)}`}>
                                  {po.status.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(po.created_at).toLocaleDateString('en-GB')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button className="text-[#2b3380] hover:text-blue-900">View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* GRN Tab */}
              {activeTab === 'grn' && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b">
                    <h2 className="text-xl font-semibold">Goods Receipt Notes</h2>
                  </div>

                  {grns.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="text-6xl mb-4">📦</div>
                      <h3 className="text-lg font-semibold mb-2">No GRNs Yet</h3>
                      <p className="text-gray-600">GRNs will be created when goods are received</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GRN Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">QC Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received Date</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {grns.map((grn) => (
                            <tr key={grn.grn_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                                  {grn.grn_number}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {grn.po_number}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {grn.supplier_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  grn.overall_qc_status === 'Pass' ? 'bg-green-100 text-green-800' :
                                  grn.overall_qc_status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {grn.overall_qc_status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(grn.status)}`}>
                                  {grn.status.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(grn.received_date).toLocaleDateString('en-GB')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button className="text-[#2b3380] hover:text-blue-900">View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create RFQ Modal */}
      {showCreateRFQModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold" style={{ color: ZOBOX_PRIMARY }}>Create RFQ</h2>
              <p className="text-sm text-gray-600 mt-1">Select approved requisitions with parts needing purchase</p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {approvedRequisitions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No approved requisitions with parts requiring purchase</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvedRequisitions.map((req) => (
                    <label
                      key={req.requisition_id}
                      className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedApprovedReqs.includes(req.requisition_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedApprovedReqs([...selectedApprovedReqs, req.requisition_id]);
                          } else {
                            setSelectedApprovedReqs(selectedApprovedReqs.filter(id => id !== req.requisition_id));
                          }
                        }}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium" style={{ color: ZOBOX_PRIMARY }}>
                          {req.requisition_number}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {req.device ? `${req.device.brand} ${req.device.model}` : 'General Requisition'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Parts needing purchase: {req.spare_parts?.filter(p => p.approval_status === 'Approved' && p.stock_status === 'Need_Purchase').length}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateRFQModal(false);
                  setSelectedApprovedReqs([]);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRFQ}
                disabled={selectedApprovedReqs.length === 0}
                className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ZOBOX_PRIMARY }}
              >
                Create RFQ ({selectedApprovedReqs.length} selected)
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default PurchaseManagement;
